import { Guid } from "guid-typescript";
import { Contact } from "../../Contacts/state/contact-interfaces"
import { FilteredDropDownModel, ImportHubspotListResponse, ListModel, TranslationList } from "shared/interfaces/core-interfaces";
import yup from '../../../../shared/validation/yupExtensions';

export interface GetPartnersResponse {
    count: number
    partners: PartnerBasicInfoTableResponse[]
}

export interface GetPartnerResponse {
    partner: PartnerDetailed
}

export interface CreatePartnerRequest {
    name: string,
    nameExtension?: string | null,
    abbreviation: string,
    phone: string,
    fax: string,
    email: string,
    invoiceEmail?: string | null,
    website: string,
    uid: string,
    registerNumber: string,
    taxNumber: string,
    linkedingPage: string,
    industrySectors: Guid[],
    partnerTypes: Guid[]
}

interface CommandResponseBase {
    id: Guid,
    message: string
}

export interface DeletePartnerReponse extends CommandResponseBase { }
export interface DeletePartnerLocationReponse extends CommandResponseBase { }
export interface CreatePartnerResponse extends CommandResponseBase { }
export interface ActivatePartnerResponse extends CommandResponseBase { }
export interface DeactivatePartnerResponse extends CommandResponseBase { }
export interface CreateBankdataResponse extends CommandResponseBase { }
export interface UpdateBankdataResponse extends CommandResponseBase { }
export interface CreateLocationResponse extends CommandResponseBase { }
export interface UpdateLocationResponse extends CommandResponseBase { }

export interface CreatePartnerDataResponse {
    partnerTypes: TranslationList[] | [],
    industrySectors: TranslationList[] | [],
    articles: TranslationList[] | [],
    employees: ListModel<string>[],
    countries: ListModel<number>[],
    banks: ListModel<string>[],
    bankContacts: Record<string, ListModel<Guid>[]>
    activePartnersList: ListModel<string>[],
    timeZones: ListModel<string>[],
    leadStatuses: ListModel<number>[],
    lifecycleStages: ListModel<number>[],
}

//---------------------------------------

export interface Partner {
    id: Guid,
    partnerOwner: string,
    name: string,
    nameExtension?: string | null,
    abbreviation: string,
    phone: string,
    fax: string,
    email: string,
    invoiceEmail?: string | null,
    website: string
}

export interface PartnerDetailed extends Partner {
    uid: string,
    registerNumber: string,
    taxNumber: string,
    linkedInPage: string,
    annualRevenue: number | null,
    numberOfEmployees: number | null,
    placeOfJurisdiction: string,
    bankData: BankData[],
    locations: Location[],
    partnerTypes: ListModel<string>[],
    industrySectors: ListModel<string>[],
    contacts: Contact[],
    description: string | null,
    clientNumber?: string | null,
    supplierNumber?: string | null,
    partnerBanksBankData?: BankData[],
    articles?: Record<string, string>,
    timeZone?: string | null,
    yearFounded?: number | null,
    facebookCompanyPage?: string | null,
    instagramPage?: string | null;
    lifecycleStage?: number | null,
    leadStatus?: number | null,
    childCompanies?: ListModel<string>[] | null,
    parentCompanies?: ListModel<string>[] | null,
    logoURL?: string | null
}

export interface PartnerBasicInfo {
    id: Guid | null,
    partnerOwner: string,
    name: string,
    nameExtension?: string | null,
    abbreviation?: string | null,
    phone?: string | null,
    fax: string | null,
    email?: string | null,
    invoiceEmail?: string | null,
    website: string | null,
    uid: string,
    registerNumber?: string | null,
    taxNumber?: string | null,
    linkedInPage: string | null,
    description: string | null,
    annualRevenue: number | null,
    numberOfEmployees: number | null,
    placeOfJurisdiction: string | null,
    industrySectors: ListModel<string>[],
    partnerTypes: ListModel<string>[],
    hasLocations: boolean,
    hasBankData: boolean,
    clientNumber: string,
    supplierNumber: string,
    articlesForFront?: string[] | [],
    articles?: Record<string, string>;
    logoURL?: string | null;
    timeZone?: string | null;
    yearFounded?: number | null;
    facebookCompanyPage?: string | null;
    instagramPage?: string | null;
    lifecycleStage?: number | null;
    leadStatus?: number | null;
    childCompanies?: ListModel<string>[] | null,
    parentCompanies?: ListModel<string>[] | null,
}

export interface PartnerBasicInfoTableResponse {
    id: Guid | null,
    partnerOwner: string,
    name: string,
    nameExtension?: string | null,
    abbreviation?: string | null,
    phone?: string | null,
    fax: string | null,
    email?: string | null,
    invoiceEmail?: string | null,
    website: string | null,
    uid: string | null,
    registerNumber?: string | null,
    taxNumber?: string | null,
    linkedInPage: string | null,
    description: string | null,
    annualRevenue: number | null,
    numberOfEmployees: number | null,
    placeOfJurisdiction: string | null,
    hasLocations: boolean,
    hasBankData: boolean,
    clientNumber: string,
    supplierNumber: string,
    logoURL?: string | null;
    timeZone?: string | null;
    yearFounded?: number | null;
    facebookPage?: string | null;
    instagramPage?: string | null;
    lifecycleStage?: number | null;
    leadStatus?: number | null;
    childCompanies?: ListModel<string>[] | null,
    parentCompanies?: ListModel<string>[] | null,

    articles: TranslationList[];
    partnerTypes: TranslationList[];
    industrySectors: TranslationList[];
}

// ----------------------------------------
// Drafts are companies an AI agent prepared and nobody has confirmed yet. Only the name is
// guaranteed; every other field may be missing, so all of them are nullable here.

export interface GetPartnerDraftsResponse {
    count: number
    drafts: PartnerDraftListItem[]
}

export interface GetPartnerDraftResponse {
    draft: PartnerDraft
}

export interface PartnerDraftListItem {
    id: string,
    name?: string | null,
    nameExtension?: string | null,
    abbreviation?: string | null,
    phone?: string | null,
    email?: string | null,
    website?: string | null,
    uid?: string | null,
    registerNumber?: string | null,
    taxNumber?: string | null,
    description?: string | null,
    creationTimestampUnix?: number | null
}

export interface PartnerDraft extends PartnerDraftListItem {
    fax?: string | null,
    invoiceEmail?: string | null,
    linkedInPage?: string | null,
    instagramPage?: string | null,
    facebookCompanyPage?: string | null,
    placeOfJurisdiction?: string | null,
    annualRevenue?: number | null,
    numberOfEmployees?: number | null,
    yearFounded?: number | null,
    logoURL?: string | null,
    timeZone?: string | null,
    lifecycleStage?: number | null,
    leadStatus?: number | null,
    location?: PartnerDraftLocation | null
}

/**
 * The draft as the user completed it in the company form. The backend applies the rules of creating a
 * company; a null location means the company gets none and a location the draft had is removed.
 */
export interface ConfirmPartnerDraftRequest extends Omit<CreatePartnerBasicInfoRequest, "id"> {
    id: string,
    location: PartnerDraftLocationRequest | null
}

/** The confirmed company's primary location (the backend always makes it primary). */
export interface PartnerDraftLocationRequest {
    locationName: string,
    country: string,
    // 0 while no country is chosen, as in the company location form.
    countryId: number,
    state?: string | null,
    city: string,
    zipCode: string,
    streetAndNumber: string,
    addressExtension?: string | null,
    latitude?: number | null,
    longitude?: number | null,
    isInvoiceLocation: boolean
}

export const partnerDraftLocationRequestSchema = yup.object().shape({
    locationName: yup.string().required("Name is required."),
    country: yup.string().defined(),
    countryId: yup.number().required("Country is required.").moreThan(0, 'Country is required.'),
    state: yup.string().nullable(),
    city: yup.string().required("City is required."),
    zipCode: yup.string().required("Zip code is required."),
    streetAndNumber: yup.string().required("Street and number are required."),
    addressExtension: yup.string().noLeadingSpace().nullable(),
    latitude: yup.number().nullable(),
    longitude: yup.number().nullable(),
    isInvoiceLocation: yup.boolean().defined()
})

export interface ConfirmPartnerDraftResponse extends CommandResponseBase { }
export interface DiscardPartnerDraftResponse extends CommandResponseBase { }

export interface PartnerDraftLocation {
    id: string,
    locationName?: string | null,
    country?: string | null,
    countryId?: number | null,
    state?: string | null,
    city?: string | null,
    zipCode?: string | null,
    streetAndNumber?: string | null,
    addressExtension?: string | null,
    latitude?: number | null,
    longitude?: number | null,
    isInvoiceLocation?: boolean | null
}

export interface GetBanksResponse {
    banks: ListModel<string>[],
    bankContacts: Record<string, ListModel<Guid>[]>
}

// ----------------------------------------

export interface BankData {
    id: Guid,
    accountName: string | null,
    bankAccountNumber: string,
    iban: string,
    swiftBic: string,
    isPrimary: boolean,
    isActive: boolean,
    bankContactPerson: ListModel<Guid>,
    bank: ListModel<Guid>
}

export interface GetPartnerLocationsResponse {
    locations: Location[]
}

export interface GetCountriesResponse {
    countries: ListModel<number>[]
}

export interface Location {
    id: string,
    partnerId: string,
    locationName: string,
    country: string,
    countryid?: number | null;
    city: string,
    state?: string | null,
    zipCode: string,
    streetAndNumber: string,
    addressExtension: string | null;
    latitude?: number | null,
    longitude?: number | null,
    isPrimary: boolean,
    isInvoiceLocation: boolean,
    isActive: boolean,
}

export interface PartnerType {
    id: Guid,
    name: string,
    description: string
}

export interface IndustrySector {
    id: Guid,
    name: string,
    description: string
}

export interface PartnerSliceModel {
    statusCode: string,
    isLoading: boolean,
    isModalLoading: boolean,
    isModalSubmited: boolean,
    message: string,
    showNotification: boolean,
    activePartnersCount: number,
    activePartners: PartnerBasicInfoTableResponse[],
    inactivePartnersCount: number,
    inactivePartners: PartnerBasicInfoTableResponse[],
    draftPartners: PartnerDraftListItem[],
    partnerTypes: PartnerType[],
    industrySectors: IndustrySector[],
    partner: PartnerDetailed | null,
    partnerToShow: PartnerDetailed | null,
    partnerBasicInfo: CreatePartnerBasicInfoRequest | null,
    partnerBankData: BankData[],
    partnerLocations: Location[],
    bankDataForUpdate: BankDataRequest | null,
    locationForUpdate: PartnerLocationRequest | null,
    createPartnerData: CreatePartnerDataResponse,
    reloadBankData: boolean,
    reloadLocation: boolean,
    reloadData: boolean,
    importPartners: {
        importedPartnersResponse: ImportHubspotListResponse | null,
        isLoading: boolean,
    },
    filteredPartnersList: FilteredDropDownModel,
}

export interface LocationHelperModel {
    id: Guid,
    name: string
}

export interface PartnerHelperModel {
    id: Guid,
    name: string,
    locations: LocationHelperModel[]
}

export interface CreatePartnerBasicInfoRequest {
    id: Guid | null,
    partnerOwner: string,
    name: string,
    nameExtension?: string | null,
    abbreviation?: string | null,
    phone?: string | null,
    fax: string | null,
    email?: string | null,
    invoiceEmail?: string | null,
    website: string | null,
    uid: string,
    registerNumber?: string | null,
    taxNumber?: string | null,
    linkedInPage: string | null,
    description: string | null,
    annualRevenue: number | null,
    numberOfEmployees: number | null,
    placeOfJurisdiction: string | null,
    industrySectors: ListModel<string>[],
    partnerTypes: ListModel<string>[],
    hasLocations: boolean,
    hasBankData: boolean,
    clientNumber: string,
    supplierNumber: string,
    articlesForFront?: string[] | [],
    articles?: Record<string, string>;
    logoURL?: string | null;
    timeZone?: string | null;
    yearFounded?: number | null;
    facebookCompanyPage?: string | null;
    instagramPage?: string | null;
    lifecycleStage?: number | null;
    leadStatus?: number | null;
    childCompanies?: ListModel<string>[] | null,
    parentCompanies?: ListModel<string>[] | null,
}

export const initialCreatePartnerValue: PartnerBasicInfo =
{
    id: null,
    partnerOwner: "",
    name: "",
    nameExtension: null,
    abbreviation: null,
    phone: "",
    fax: "",
    email: "",
    invoiceEmail: null,
    website: "",
    uid: "",
    logoURL: null,
    registerNumber: "",
    taxNumber: "",
    linkedInPage: "",
    description: "",
    annualRevenue: null,
    numberOfEmployees: null,
    placeOfJurisdiction: "",
    industrySectors: [],
    partnerTypes: [],
    hasBankData: false,
    hasLocations: false,
    instagramPage: null,
    clientNumber: "",
    supplierNumber: "",
    articlesForFront: [],
    articles: {}
}

export const PartnerBasicInfoRequestSchema = yup.object().shape({
    id: yup.mixed<Guid>().defined().nullable(),
    partnerOwner: yup.string().required("Company owner is required.").defined().min(1, "Choose company owner."),
    name: yup.string().noLeadingSpace().required("Company name is required."),
    nameExtension: yup.string().nullable(),
    abbreviation: yup
        .string()
        .nullable()
        .test(
            'is-valid-abbreviation',
            'Max 15 chars: letters, numbers, spaces, and symbols allowed.',
            (value) => !value || /^[\w\s!@#$%^&*()\-_=+[{\]};:'",.<>/?]{1,15}$/.test(value)
        ),
    phone: yup.string().validInternationalPhoneNumber().validPhoneNumber().nullable(),
    fax: yup.string().defined().nullable(),
    email: yup.string().email("Please enter valid email.").nullable(),
    invoiceEmail: yup.string().email("Please enter valid email.").nullable(),
    website: yup.string().defined().validWebsite('This URL format is invalid.').nullable(),
    uid: yup.string().defined().required("VAT ID is required."),
    registerNumber: yup.string().nullable(),
    taxNumber: yup.string().nullable(),
    linkedInPage: yup.string().defined().url('This URL format is invalid.').nullable(),
    description: yup.string().defined().nullable(),
    annualRevenue: yup.number()
        .defined()
        .transform((value) => Number.isNaN(value) ? null : value)
        .nullable(),
    numberOfEmployees: yup.number()
        .integer()
        .transform((value) => Number.isNaN(value) ? null : value)
        .min(0, "Number of employees must be greater than 0.")
        .defined()
        .nullable(),
    placeOfJurisdiction: yup.string().defined().nullable(),
    industrySectors: yup.array<ListModel<Guid>>().min(1, "At least one industry sector must be selected.").required("Industry sector is required."),
    partnerTypes: yup.array<ListModel<Guid>>().min(1, "At least one company type must be selected.").required("Company type is required."),
    hasBankData: yup.boolean().required(),
    hasLocations: yup.boolean().required(),
    clientNumber: yup.string().defined(),
    supplierNumber: yup.string().defined(),
    articles: yup.mixed(),
    articlesForFront: yup.array(),
    logoURL: yup.string().url('This URL format is invalid.').nullable(),
    timeZone: yup.string().nullable(),
    yearFounded: yup.number().integer('Must be a valid year.').transform((value) => Number.isNaN(value) ? null : value).min(1000, 'Year must be after 999.').max(new Date().getFullYear(), 'Year cannot be in the future.').nullable(),
    facebookCompanyPage: yup.string().url('This URL format is invalid.').nullable(),
    instagramPage: yup.string().url('This URL format is invalid.').nullable(),
    lifecycleStage: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    leadStatus: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    childCompanies: yup.array<ListModel<string>>().nullable(),
    parentCompanies: yup.array<ListModel<string>>().nullable(),
});

export interface BankDataRequest {
    id: string | null,
    partnerId: string,
    partnerBankId: string,
    bankContactPersonId: string | null,
    accountName: string | null,
    bankAccountNumber: string | null,
    ibanBase: string | null,
    ibanCountryPrefix: string | null,
    swiftBic: string,
    comment: string | null,
    isPrimary: boolean,
    routingNumber?: string,
    ibanValidation: {},
    customValidation: {}
}

export const BankDataRequestSchema = yup.object().shape({
    id: yup.string().defined().nullable(),
    partnerId: yup.string().defined().min(1, "").required(),
    partnerBankId: yup.string().defined().min(1, "Bank is required.").required("Bank is required."),
    bankContactPersonId: yup.mixed<string>().defined().nullable(),
    accountName: yup.string().defined().nullable(),
    bankAccountNumber: yup.string().defined().nullable(),
    ibanBase: yup.string().matches(/^[a-zA-Z0-9 ]{11,28}$|^$/, "Iban number invalid.").defined().nullable(),
    ibanCountryPrefix: yup.string().defined().nullable().matches(/^[A-Z]{2}(?:[ ]?[0-9]){2}$|^$/, "Country Prefix invalid."),
    swiftBic: yup.string().trim().required("SWIFT/BIC is required.").min(8, "SWIFT/BIC must be at least 8 characters.").max(11, "SWIFT/BIC must be at most 11 characters."),
    comment: yup.string().defined().nullable(),
    isPrimary: yup.boolean().defined().required(),
    ibanValidation: yup.object().test({
        name: "ibanValidation",
        message: "Please provide either both IBAN Country Prefix and IBAN Base or none.",
        test: function () {
            const ibanBase = this.parent.ibanBase;
            const ibanCountryPrefix = this.parent.ibanCountryPrefix;

            return (!ibanBase && !ibanCountryPrefix) || (ibanBase && ibanCountryPrefix);
        }
    }),
    customValidation: yup.object().test({
        name: "customValidation",
        message: "It is mandatory to enter either IBAN or Bank Account Number.",
        test: function () {
            const hasIBAN = this.parent.ibanBase && this.parent.ibanCountryPrefix;
            const hasBankAccount = this.parent.bankAccountNumber;

            return hasIBAN || hasBankAccount;
        }
    })
});

export interface PartnerLocationRequest {
    id?: string | null,
    partnerId: string,
    locationName: string,
    longitude?: number | null,
    latitude?: number | null,
    country?: string,
    countryId: number,
    city: string,
    state?: string | null,
    zipCode: string,
    streetAndNumber: string,
    addressExtension?: string | null;
    isPrimary: boolean,
    isInvoiceLocation: boolean
}

export const partnerLocationRequestSchema = yup.object().shape({
    id: yup.string().nullable(),
    partnerId: yup.string().defined().min(1, "").required(),
    locationName: yup.string().required("Name is required."),
    longitude: yup.number().nullable(),
    latitude: yup.number().nullable(),
    countryId: yup.number().required("Country is required.").moreThan(0, 'Country is required.'),
    city: yup.string().required("City is required."),
    state: yup.string().nullable(),
    zipCode: yup.string().required("Zip code is required."),
    streetAndNumber: yup.string().required("Street and number are required."),
    addressExtension: yup.string().noLeadingSpace().nullable(),
    isPrimary: yup.boolean().defined(),
    isInvoiceLocation: yup.boolean().defined()
})

